import { PropsWithChildren, useMemo, useRef } from 'react';
import { PlaybackProvider } from '../context/PlaybackProvider';
import { LiveUrlAudioPlayer } from './live-url-audio-player';
import { createAudioPlaybackStore } from './useAudioPlaybackStore';

/**
 * Provider component that creates a LiveUrlAudioPlayer and passes it to the audio playback store.
 * Used for live URL-based audio playback (e.g., live streams).
 * The player manages audio elements internally, creating a new one on each play() call.
 */
export function LiveUrlAudioPlaybackProvider({
  src,
  children,
  playbackId,
}: PropsWithChildren<{
  /** Source URL for live audio playback */
  src: string;
  /** Optional identifier for this playback instance, shown in debug logs */
  playbackId?: string;
}>) {
  const adapterRef = useRef<LiveUrlAudioPlayer | null>(null);

  // Create store with adapter exactly once per component instance
  const store = useMemo(() => {
    // Create adapter with src
    adapterRef.current = new LiveUrlAudioPlayer(src, {
      playbackId,
    });

    return createAudioPlaybackStore({
      player: adapterRef.current,
      src,
      playbackId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <PlaybackProvider store={store}>{children}</PlaybackProvider>;
}
